import {
  buildFeatureFlags,
  applyPageConfigOverrides,
  parseExperiencesHeader,
  FeatureFlagValue,
} from "@packages/feature-flags";
import { Product, ResultsInfo } from "./types/types";
import { layoutMap as listLayouts } from "./product-list-layouts/layoutMap";
import { raise } from "./utils/raise";
import defaultFlagSettings from "./flags/defaultFlagSettings.json";
import { useSocialProofMessaging } from "./product-card-components/SocialProofMessaging/useSocialProofMessaging";
import { SocialProofMessagesProvider } from "./contexts/SocialProofMessagesContext/SocialProofMessagesContext";
import { FeatureFlagsProvider } from "./flags/flags";
import { productListConfigMap } from "./configs/productListConfigMap";

export function ProductList({
  products,
  config: configName,
  experiencesRawString,
  resultsInfo,
  instanceId,
  heading,
  flags,
}: {
  config: keyof typeof productListConfigMap;
  products: Product[];
  experiencesRawString?: string;
  resultsInfo: ResultsInfo;
  instanceId: string;
  heading?: {
    main: string;
    subHeading?: string;
  };
  flags?: Record<string, FeatureFlagValue>;
}) {
  const config =
    productListConfigMap[configName] ||
    raise(`Couldn't find product-list config "${configName}"`);

  const { name: listLayoutName, props: listLayoutProps } =
    config["product-list-layout"];

  const ListLayout =
    listLayouts[listLayoutName] ||
    raise(`Couldn't find product-list layout "${listLayoutName}"`);

  const experiences = parseExperiencesHeader(experiencesRawString || "");
  const flagsWithoutPageConfigValues = buildFeatureFlags(
    experiences,
    defaultFlagSettings,
    `product-list-${instanceId}-`,
    undefined, // TODO get cookie flags?
    flags,
  );
  const combinedFlags = applyPageConfigOverrides(
    flagsWithoutPageConfigValues,
    config.flags,
  );

  const socialProofComponent = config["product-card-slots"].banner?.filter(
    (bslot) => bslot.name === "SocialProofMessaging",
  );

  const socialProofMessages = useSocialProofMessaging({
    resultsInfo,
    products,
    appId: `${(socialProofComponent && socialProofComponent[0]?.props?.appId) || ""}`,
  });

  return (
    <FeatureFlagsProvider
      flags={combinedFlags}
      experiencesRawString={experiencesRawString}
      id={`product-list-${instanceId}`}
    >
      <SocialProofMessagesProvider messages={socialProofMessages}>
        <ListLayout
          config={config}
          products={products}
          configProps={listLayoutProps}
          resultsInfo={resultsInfo}
          heading={heading}
        />
      </SocialProofMessagesProvider>
    </FeatureFlagsProvider>
  );
}
